import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { removeAllEntities, upsertEntity, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AbilityService } from 'ability';
import { cloneDeep } from 'lodash';
import { getGeneralHostInfo } from 'mbs-ui-shared@components/computers-shared/get-general-host-Info';
import { LicenseType } from 'mbs-ui-shared@models';
import Computer, { AgentType, OsType } from 'mbs-ui-shared@models/Computer';
import { PlanFormatTypes, PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { AvailablePlanTypes, KindPlanEnum } from 'mbs-ui-shared@models/backup/available-plan-types';
import { RepositorySyncState } from 'mbs-ui-shared@models/rm/remote-command-model';
import { ComputersAbstractService } from 'mbs-ui-shared@services/computers.service';
import { RmCommandsWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import { isDataObsolete } from 'mbs-ui-shared@utils/store';
import moment from 'moment';
import { EMPTY, Observable, of, pipe } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  ComputerDetails,
  ComputersBackupState,
  initialComputerBackupData,
  initialComputerDetails,
  initialComputersBackupState,
  initialDestinationBackupData
} from './state';

const OBSOLESCENCE_TIMEOUT = 1; // minutes
const PROXMOX_READY_AGENT_VERSION = 85041;

function ensureEntity(entityMap: Record<string, ComputerDetails>, hid: string): ComputerDetails {
  return cloneDeep(entityMap[hid] || { ...initialComputerDetails, id: hid });
}

export const ComputersBackupStore = signalStore(
  { providedIn: 'root' },
  withEntities<ComputerDetails>(),
  withState<ComputersBackupState>(initialComputersBackupState),
  withDevtools('ComputersBackupStore'),
  withMethods((store) => {
    const computersService = inject(ComputersAbstractService);
    const rmCommands = inject(RmCommandsWrapper);
    const ability = inject(AbilityService);

    function getAvailablePlans$(computer: Computer): Observable<AvailablePlanTypes> {
      return computer?.hid &&
        computer?.online &&
        Computer.isAgentOnline(computer, AgentType.Backup) &&
        (computer?.os === OsType.windows || Computer.isNetBuild(computer))
        ? rmCommands.getAvailablePlanTypes(computer.hid).pipe(
            map((plans) => filterInstantRestore(plans)),
            map((plans) => filterRestoreSystemState(plans)),
            map((plans) => filterCBFIfNotCanLegacy(plans)),
            catchError(() => getCalculatedAvailablePlanTypes$(computer))
          )
        : getCalculatedAvailablePlanTypes$(computer);
    }

    function filterInstantRestore(plans: AvailablePlanTypes): AvailablePlanTypes {
      const instantRestorePlanTypes: PlanTypes[] = [
        PlanTypes.InstantRestoreHyperV,
        PlanTypes.InstantRestoreProxmox,
        PlanTypes.InstantRestoreMSP360LDR
      ];

      return plans.filter((p) => !instantRestorePlanTypes.includes(p.planType as PlanTypes));
    }

    function filterRestoreSystemState(plans: AvailablePlanTypes): AvailablePlanTypes {
      return ability.can('read', 'EnableSystemState') ? plans : plans.filter((plan) => plan.planType !== PlanTypes.RestoreSystemState);
    }

    function filterCBFIfNotCanLegacy(plans: AvailablePlanTypes): AvailablePlanTypes {
      const invalidCbfPlanTypes: PlanTypes[] = [
        PlanTypes.Plan,
        PlanTypes.BackupDiskImagePlan,
        PlanTypes.BackupHyperV,
        PlanTypes.BackupVMware
      ];

      return ability.can('read', 'ShowLegacyPlans')
        ? plans
        : plans.filter((plan) => plan.format !== PlanFormatTypes.CBF || !invalidCbfPlanTypes.includes(plan.planType as PlanTypes));
    }

    function getCalculatedAvailablePlanTypes$(computer: Computer): Observable<AvailablePlanTypes> {
      const isWindows = computer.os === OsType.windows;
      const isNetBuild = Computer.isNetBuild(computer);
      const canLegacy = ability.can('read', 'ShowLegacyPlans');
      const isSQL = Computer.isUltimateOrSQLServer(computer);
      const types: AvailablePlanTypes = [];

      if ((isWindows || isNetBuild) && computer?.backupLicense?.licenseType === LicenseType.VirtualMachine) {
        if (isNetBuild) {
          if (canLegacy) types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.BackupHyperV, format: PlanFormatTypes.CBF });
          types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.BackupHyperV, format: PlanFormatTypes.NBF });
        }
        if (canLegacy) types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.BackupVMware, format: PlanFormatTypes.CBF });
        types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.BackupVMware, format: PlanFormatTypes.NBF });
        if (Computer.IsSupportedAgentVersion(computer, AgentType.Backup, PROXMOX_READY_AGENT_VERSION, true)) {
          types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.BackupProxmox, format: PlanFormatTypes.NBF });
        }
      }
      types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.Plan, format: PlanFormatTypes.NBF });
      if (canLegacy) types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.Plan, format: PlanFormatTypes.CBF });
      if (isSQL && computer.online) types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.DataBasePlan });
      if (isWindows || isNetBuild) {
        types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.BackupDiskImagePlan, format: PlanFormatTypes.NBF });
        if (canLegacy) types.push({ kind: KindPlanEnum.Backup, planType: PlanTypes.BackupDiskImagePlan, format: PlanFormatTypes.CBF });
        if (computer.online) types.push({ kind: KindPlanEnum.ConsistencyCheck, planType: PlanTypes.ConsistencyCheck });
      }
      types.push({ kind: KindPlanEnum.Restore, planType: PlanTypes.RestorePlan, format: PlanFormatTypes.NBF, needShow: true });
      if (isWindows || isNetBuild) {
        types.push({ kind: KindPlanEnum.Restore, planType: PlanTypes.RestoreDiskImagePlan, format: PlanFormatTypes.NBF });
      }
      if ((isWindows || isNetBuild) && computer?.backupLicense?.licenseType === LicenseType.VirtualMachine) {
        if (!isNetBuild) {
          types.push(
            { kind: KindPlanEnum.Restore, planType: PlanTypes.RestoreVMware, format: PlanFormatTypes.NBF },
            { kind: KindPlanEnum.Restore, planType: PlanTypes.RestoreHyperV, format: PlanFormatTypes.NBF }
          );
        } else {
          types.push({ kind: KindPlanEnum.Restore, planType: PlanTypes.RestoreVMware, format: PlanFormatTypes.NBF });
        }
        if (Computer.IsSupportedAgentVersion(computer, AgentType.Backup, PROXMOX_READY_AGENT_VERSION, true)) {
          types.push({ kind: KindPlanEnum.Restore, planType: PlanTypes.RestoreProxmox, format: PlanFormatTypes.NBF });
        }
      }
      if (isSQL) {
        types.push(
          { kind: KindPlanEnum.Restore, planType: PlanTypes.RestoreDataBasePlan },
          { kind: KindPlanEnum.Restore, planType: PlanTypes.RestoreDatabaseFilesPlan }
        );
      }
      return of(types);
    }

    function patchEntity(hid: string, updater: (entity: ComputerDetails) => void): void {
      const entity = ensureEntity(store.entityMap(), hid);
      updater(entity);
      patchState(store, upsertEntity(entity));
    }

    const _loadSyncStatus = rxMethod<string>(
      pipe(
        mergeMap((hid) =>
          rmCommands.getSyncStatus(hid).pipe(
            map((data) => ({ hid, syncProcess: data.state === RepositorySyncState.InProgress })),
            catchError(() => EMPTY),
            tapResponse({
              next: ({ hid, syncProcess }) => {
                patchEntity(hid, (entity) => (entity.syncProcess = syncProcess));
              },
              error: () => {}
            })
          )
        )
      )
    );

    return {
      entityByHid: (hid: string) => computed(() => store.entityMap()[hid]),

      clear(): void {
        patchState(store, removeAllEntities(), { plansStatuses: {} });
      },

      loadGeneralInfo(options: { hid: string; force?: boolean; quiet?: boolean }): void {
        const { hid, force, quiet } = options;
        const entityMap = store.entityMap();
        const entity = ensureEntity(entityMap, hid);
        const isObsolete = isDataObsolete(entity.hostInfo.obsolescenceMark, OBSOLESCENCE_TIMEOUT);

        if (isObsolete || force) entity.hostInfo = cloneDeep(initialComputerBackupData);

        entity.hostInfo = {
          ...entity.hostInfo,
          loading: !entity.hostInfo.loaded || !!force,
          visibleLoading: (!quiet && (!entity.hostInfo.loaded || !!force)) ?? false
        };
        patchState(store, upsertEntity(entity));

        if (!entity.hostInfo.loading) return;

        computersService
          .getComputerHostSystemInfo(hid)
          .pipe(
            map((data) =>
              data?.message ? null : getGeneralHostInfo(data, true, hid).filter((result) => (result.visible ? result.visible() : true))
            ),
            catchError(() => of(null))
          )
          .subscribe((data) => {
            const currentEntityMap = store.entityMap();
            if (!currentEntityMap[hid]) return;
            const updated = cloneDeep(currentEntityMap[hid]);
            if (data) {
              updated.hostInfo = {
                data,
                loaded: true,
                visibleLoading: false,
                loading: false,
                obsolescenceMark: updated.hostInfo.obsolescenceMark ?? moment().valueOf()
              };
            } else {
              updated.hostInfo.loading = false;
              updated.hostInfo.visibleLoading = false;
            }
            patchState(store, upsertEntity(updated));
          });
      },

      setGeneralInfoSuccess(hid: string, data: ReturnType<typeof getGeneralHostInfo>): void {
        const entityMap = store.entityMap();
        if (!entityMap[hid]) return;
        const updated = cloneDeep(entityMap[hid]);
        updated.hostInfo = {
          data,
          loaded: true,
          visibleLoading: false,
          loading: false,
          obsolescenceMark: updated.hostInfo.obsolescenceMark ?? moment().valueOf()
        };
        patchState(store, upsertEntity(updated));
      },

      loadDestinations(options: { hid: string; force?: boolean; quiet?: boolean; doNotIncludeLocal?: boolean }): void {
        const { hid, force, quiet, doNotIncludeLocal } = options;
        const entityMap = store.entityMap();
        const entity = ensureEntity(entityMap, hid);
        const existChanges = doNotIncludeLocal !== entity.destinations.doNotIncludeLocal;
        const isObsolete = isDataObsolete(entity.destinations.obsolescenceMark, OBSOLESCENCE_TIMEOUT);

        if (isObsolete || force || existChanges) entity.destinations = cloneDeep(initialDestinationBackupData);

        entity.destinations = {
          ...entity.destinations,
          loading: !entity.destinations.loaded || !!force,
          visibleLoading: (!quiet && (!entity.destinations.loaded || !!force)) ?? false
        };
        patchState(store, upsertEntity(entity));

        if (!entity.destinations.loading) return;

        computersService
          .getDestinationsByComputerHid(hid, options.doNotIncludeLocal)
          .pipe(catchError(() => of(null)))
          .subscribe((data) => {
            const currentEntityMap = store.entityMap();
            if (!currentEntityMap[hid]) return;
            const updated = cloneDeep(currentEntityMap[hid]);
            if (data !== null) {
              updated.destinations = {
                data: data || [],
                loaded: true,
                visibleLoading: false,
                loading: false,
                obsolescenceMark: updated.destinations.obsolescenceMark ?? moment().valueOf(),
                doNotIncludeLocal
              };
            } else {
              updated.destinations.loading = false;
              updated.destinations.visibleLoading = false;
            }
            patchState(store, upsertEntity(updated));
          });
      },

      loadPlans(options: { computer: Computer; force?: boolean; quiet?: boolean }): void {
        const { computer, force, quiet } = options;
        const hid = computer?.hid;
        const entityMap = store.entityMap();
        const entity = ensureEntity(entityMap, hid);
        const isObsolete = isDataObsolete(entity.plans.obsolescenceMark, OBSOLESCENCE_TIMEOUT);
        const wasLoaded = entity.plans.loaded;
        const previousPlansData = entity.plans.data;

        if (isObsolete || force) entity.plans = cloneDeep(initialComputerBackupData);

        entity.plans = {
          ...entity.plans,
          data: quiet ? previousPlansData : entity.plans.data,
          loading: !entity.plans.loaded || !!force,
          visibleLoading: (!quiet && (!entity.plans.loaded || !!force)) ?? false
        };
        patchState(store, upsertEntity(entity));

        if ((!wasLoaded || force) && Computer.isAgentOnline(computer, AgentType.Backup)) {
          _loadSyncStatus(hid);
        }

        if (!entity.plans.loading) return;

        rmCommands
          .getComputerPlans(hid)
          .pipe(catchError((error: HttpErrorResponse) => of({ error: error.error, planInfos: null as any, timeZoneOffset: 0 })))
          .subscribe((result) => {
            const currentEntityMap = store.entityMap();
            if (!currentEntityMap[hid]) return;
            const updated = cloneDeep(currentEntityMap[hid]);
            if (result.planInfos !== null) {
              updated.plans = {
                data: result.planInfos || [],
                loaded: true,
                visibleLoading: false,
                loading: false,
                obsolescenceMark: updated.plans.obsolescenceMark ?? moment().valueOf(),
                error: null
              };
              updated.timeZoneOffset = result.timeZoneOffset || 0;
            } else {
              updated.plans.loading = false;
              updated.plans.visibleLoading = false;
              updated.plans.error = result['error'];
            }
            patchState(store, upsertEntity(updated));
          });
      },

      loadAvailablePlans(options: { computer: Computer; force?: boolean }): void {
        const { computer, force } = options;
        if (!computer?.hid) return;

        const entityMap = store.entityMap();
        const entity = ensureEntity(entityMap, computer.hid);
        const isObsolete = isDataObsolete(entity.availablePlans.obsolescenceMark, OBSOLESCENCE_TIMEOUT);

        if (isObsolete || force) entity.availablePlans = cloneDeep(initialComputerBackupData);

        entity.availablePlans = {
          ...entity.availablePlans,
          loading: !entity.availablePlans.loaded || !!force,
          visibleLoading: (!entity.availablePlans.loaded || !!force) ?? false
        };
        patchState(store, upsertEntity(entity));

        if (!entity.availablePlans.loading) return;

        getAvailablePlans$(computer).subscribe((data) => {
          const currentEntityMap = store.entityMap();
          if (!currentEntityMap[computer.hid]) return;
          const updated = cloneDeep(currentEntityMap[computer.hid]);
          updated.availablePlans = {
            data,
            loaded: true,
            visibleLoading: false,
            loading: false,
            obsolescenceMark: updated.availablePlans.obsolescenceMark ?? moment().valueOf()
          };
          patchState(store, upsertEntity(updated));
        });
      },

      loadSyncStatus(hid: string): void {
        _loadSyncStatus(hid);
      },

      setPlanRunning(params: { id: string; running: boolean }): void {
        const plansStatuses = store.plansStatuses();
        if (!plansStatuses[params.id]) return;
        patchState(store, {
          plansStatuses: { ...plansStatuses, [params.id]: { ...plansStatuses[params.id], running: params.running } }
        });
      },

      setPlanSaving(params: { id: string; saving: boolean }): void {
        const plansStatuses = store.plansStatuses();
        if (!plansStatuses[params.id]) return;
        patchState(store, {
          plansStatuses: { ...plansStatuses, [params.id]: { ...plansStatuses[params.id], saving: params.saving } }
        });
      }
    };
  })
);

export type ComputersBackupStoreType = InstanceType<typeof ComputersBackupStore>;
